import Vue from 'vue';
import App from './App.vue';
import router, {isPublicPath} from './router.js';
import store from './store'
import {timeAgo, formatDateOnly} from './utils/dates';

Vue.config.productionTip = false;

import Buefy from 'buefy';

// All options available at: https://buefy.org/documentation/constructor-options
Vue.use(Buefy, {
    defaultIconPack: 'fas'
});

import InfiniteLoading from 'vue-infinite-loading';
Vue.use(InfiniteLoading, {
    props: {
        spinner: 'waveDots'
    },
    system: {
        throttleLimit: 500
    }
});

import VueMoment from 'vue-moment';
Vue.use(VueMoment);

import JsonViewer from 'vue-json-viewer';
Vue.use(JsonViewer);

import VueGraph from 'vue-graph';
Vue.use(VueGraph);

import VueMask from 'v-mask'
Vue.use(VueMask, {
    placeholders: {
        'Z': /^[a-zA-Z0-9\-_!#$%&'*+/=?`{|}~^.-@]+/ // valid email char
    }
})

import VueCurrencyFilter from 'vue-currency-filter';
Vue.use(VueCurrencyFilter,
    {
        symbol : '$',
        thousandsSeparator: ',',
        fractionCount: 2,
        fractionSeparator: '.',
        symbolPosition: 'front',
        symbolSpacing: false
    });

import VueClipboard from 'vue-clipboard2';
VueClipboard.config.autoSetContainer = true;
Vue.use(VueClipboard);

import IdleVue from 'idle-vue';
const eventsHub = new Vue();

Vue.use(IdleVue, {
    store,
    eventEmitter: eventsHub,
    idleTime: 180000,
    startAtIdle: false
});

import contenteditable from 'vue-contenteditable'
Vue.use(contenteditable);

import Notifications from '@/components/notification';
Vue.use(Notifications);

import {axiosInstance} from "./http_client";

const token = localStorage.getItem('token');
// 'undefined'/'null' are truthy strings a bad setItem() can leave behind; sending
// one as the Authorization header 401s everything. Treat it as no session.
if (token === 'undefined' || token === 'null') {
    console.warn('Discarding invalid stored token.');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
} else if (token) {
    console.log('Setting token.');
    axiosInstance.defaults.headers.common['Authorization'] = token;
}

axiosInstance.interceptors.response.use(function (response) {
    return response;
}, function (err) {
    if (err.response && err.response.status === 401) {
        // Only tear down the session the failed call actually belongs to. A
        // dying session's in-flight requests must not sign out the session that
        // replaced it (e.g. a portal "login as user" relay in a new tab).
        const sent = err.config && err.config.headers && err.config.headers['Authorization'];
        const current = localStorage.getItem('token');
        if (sent && current && sent !== current) {
            console.log('Received 401 for a superseded session, ignoring.');
            return Promise.reject(err);
        }
        console.log('Received 401, signing out.');
        store.dispatch('signOut');
        // A stale session must not bounce visitors off public pages (an
        // e-mail's unsubscribe link, Terms, Privacy...). Read the hash: on a
        // fresh load the boot calls 401 before the router has resolved.
        const path = (window.location.hash || '#/').slice(1).split('?')[0] || '/';
        // Help Center and ticket links (support reply e-mails) should land there
        // after login (Login.vue honours redirectTo), not on the dashboard
        if (/^\/support\/(help|topic|tickets)\//.test(path)) localStorage.setItem('redirectTo', path);
        if (path !== '/' && !isPublicPath(path)) router.push('/');
    }
    return Promise.reject(err);
});

new Vue({
    router,
    store,
    render: h => h(App)
}).$mount('#app');

Vue.filter('tel', function (phone) {
    if (phone) {
        return phone.replace(/[^0-9]/g, '').replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
    } else {
        return 'N/A';
    }
});

Vue.filter('currency', function (value) {
    if (value === null || value === undefined) return '$0.00';
    const num = parseFloat(value);
    if (isNaN(num)) return '$0.00';
    return '$' + num.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
});

// Whole-dollar variant ($0,000) for marketing-facing figures
Vue.filter('money', function (value) {
    const num = parseFloat(value);
    if (isNaN(num)) return '$0';
    return '$' + Math.round(num).toLocaleString('en-US');
});

// "3 days ago" for instants, "March 3rd, 2026" for DATE columns (UTC-safe)
Vue.filter('timeAgo', timeAgo);
Vue.filter('dateOnly', formatDateOnly);

Vue.filter('capitalize', function (value) {
    if (value) {
        return value.toLowerCase().replace(/(?:^|\s|-)\S/g, x => x.toUpperCase());
    } else {
        return '';
    }
});

Vue.filter('formatSize', function (num) {
    // jacked from: https://github.com/sindresorhus/pretty-bytes
    if (typeof num !== 'number' || isNaN(num)) {
        throw new TypeError('Expected a number');
    }

    let exponent;
    let unit;
    let neg = num < 0;
    let units = ['B', 'kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    if (neg) {
        num = -num;
    }

    if (num < 1) {
        return (neg ? '-' : '') + num + ' B';
    }

    exponent = Math.min(Math.floor(Math.log(num) / Math.log(1000)), units.length - 1);
    num = (num / Math.pow(1000, exponent)).toFixed(2) * 1;
    unit = units[exponent];

    return (neg ? '-' : '') + num + ' ' + unit;
});
